import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ITicketTileDetailForm,
  ITicketTileForm,
} from '../../entities/sortiment-form.entity';
import { Factory } from '../factory';
import { FilterTextsPipe } from '../../pipes/filterTexts/filterTexts.pipe';
import {
  deriveTicketIsActive,
  ERelationType,
  INormalText,
  ITicket,
  ITicketDetail,
} from '@web/dtos';
import { IAccessibleImageForm } from '../../entities/image-entity';

@Injectable({
  providedIn: 'root',
})
export class TicketTileFactoryService extends Factory<
  ITicketTileForm,
  ITicket
> {
  formBuilder: FormBuilder = inject(FormBuilder);
  filterTexts: FilterTextsPipe = inject(FilterTextsPipe);

  buildEntity(form: FormGroup<ITicketTileForm>): ITicket {
    const ticket: any = form.getRawValue() as unknown as ITicket;
    ticket.isActive = deriveTicketIsActive(ticket.ticketCategoryId);
    if (ticket.accessibleImage) {
      if (!ticket.displayImage || !ticket.accessibleImage.image) {
        ticket.accessibleImage.image = null;
      }
    }
    const newTicket: any = {
      ...ticket,
      texts: [...ticket.title, ...ticket.description, ...ticket.detailsTitle],
    };
    delete newTicket.title;
    delete newTicket.description;
    delete newTicket.detailsTitle;
    delete newTicket.displayImage;
    this.removeNullIds(newTicket);
    return newTicket as ITicket;
  }

  buildForm(entity: ITicket | undefined): FormGroup<ITicketTileForm> {
    return this.formBuilder.group({
      id: this.formBuilder.nonNullable.control(entity?.id),
      ticketId: this.formBuilder.nonNullable.control(entity?.ticketId ?? '', [
        Validators.required,
      ]),
      serviceAreaId: this.formBuilder.nonNullable.control(
        entity?.serviceAreaId
      ),
      isActive: this.formBuilder.nonNullable.control(
        deriveTicketIsActive(entity?.ticketCategoryId)
      ),
      startDate: this.formBuilder.control(
        entity?.startDate ? new Date(entity?.startDate) : null,
        []
      ),
      endDate: this.formBuilder.control(
        entity?.endDate ? new Date(entity?.endDate) : null,
        []
      ),
      displayImage: this.formBuilder.nonNullable.control<boolean>(
        Boolean(entity?.accessibleImage?.image)
      ),
      displayPrice: this.formBuilder.nonNullable.control<boolean>(
        entity?.displayPrice ?? true
      ),
      ticketCategoryId: this.formBuilder.control(
        entity?.ticketCategoryId ?? null
      ),
      positionInCategory: this.formBuilder.control(
        entity?.positionInCategory ?? null
      ),
      title: this.formBuilder.nonNullable.control<INormalText[]>(
        this.filterTexts.transform(entity?.texts ?? [], ERelationType.title)
      ),
      accessibleImage: this.formBuilder.group<IAccessibleImageForm>({
        id: this.formBuilder.control(entity?.accessibleImage?.id || null),
        image: this.formBuilder.control(entity?.accessibleImage?.image || null),
        imageAlts: this.formBuilder.nonNullable.control(
          entity?.accessibleImage?.imageAlts ?? []
        ),
        url: this.formBuilder.control(entity?.accessibleImage?.url || null),
        positionOrder: this.formBuilder.control(1, []),
      }),
      description: this.formBuilder.nonNullable.control<INormalText[]>(
        this.filterTexts.transform(
          entity?.texts ?? [],
          ERelationType.description
        )
      ),
      detailsTitle: this.formBuilder.nonNullable.control<INormalText[]>(
        this.filterTexts.transform(
          entity?.texts ?? [],
          ERelationType.detailsTitle
        )
      ),
      details: this.formBuilder.nonNullable.array(
        entity?.details?.map(detail =>
          this.createTicketTileDetailForm(detail)
        ) ?? []
      ),
    });
  }

  public createTicketTileDetailForm(
    detail: ITicketDetail | undefined = undefined
  ): FormGroup<ITicketTileDetailForm> {
    return this.formBuilder.group({
      id: this.formBuilder.nonNullable.control(detail?.id, []),
      icon: this.formBuilder.nonNullable.control(detail?.icon ?? 'check', []),
      type: this.formBuilder.nonNullable.control(detail?.type ?? 'text'),
      link: this.formBuilder.nonNullable.control(detail?.link ?? ''),
      sortPosition: this.formBuilder.nonNullable.control(
        detail?.sortPosition ?? 0,
        [Validators.required]
      ),
      texts: this.formBuilder.nonNullable.control<INormalText[]>(
        this.filterTexts.transform(detail?.texts ?? [], ERelationType.text) ??
          []
      ),
    });
  }
}
